import { DeviceInfo } from '../types';

export interface VideoJSConfigOptions {
  videoId: string;
  videoUrl: string;
  deviceInfo: DeviceInfo;
  autoPlay?: boolean;
  muted?: boolean;
  quality?: string;
  useStreaming?: boolean;
  liveMode?: boolean;
}

export const createVideoJSConfig = ({
  videoId,
  videoUrl,
  deviceInfo,
  autoPlay = false,
  muted = false,
  quality = 'auto',
  useStreaming = true,
  liveMode = false,
}: VideoJSConfigOptions): Record<string, unknown> => {
  // 動画URLから適切なMIMEタイプを判定
  const getVideoType = (url: string): string => {
    const urlLower = url.toLowerCase();
    
    // HLS形式のチェック
    if (urlLower.includes('.m3u8') || urlLower.includes('hls')) {
      return 'application/x-mpegURL';
    }
    
    // 明示的な拡張子によるチェック
    if (urlLower.includes('.mp4')) {
      return 'video/mp4';
    }

    if (urlLower.includes('.webm')) {
      return 'video/webm';
    }

    // ストリーミングAPIはMP4として扱う（Chrome/Firefox/Edgeはサーバーのレスポンスで実際のコーデックを判定）
    if (urlLower.includes('/stream/') || urlLower.includes('video/stream')) {
      return 'video/mp4';
    }

    return 'video/mp4';
  };

  const videoType = getVideoType(videoUrl);

  const baseConfig = {
    // 基本設定
    controls: true,
    responsive: true,
    fluid: true,
    playsinline: true,
    preload: (deviceInfo.connectionType === 'wifi' ? 'auto' : 'metadata') as 'auto' | 'metadata' | 'none',
    
    // ソース設定
    sources: [{
      src: videoUrl,
      type: videoType
    }],
    
    // 自動再生設定
    autoplay: autoPlay,
    muted: muted,
    
    // ライブストリーム設定（liveMode時は live edge に追従）
    liveui: liveMode,
    loop: false,
    
    // ローディングスピナーを無効化（カスタムローディング表示を使用）
    loadingSpinner: false,
    
    // コントロールバーの設定（YouTubeライク）
    controlBar: {
      // 基本コントロール
      playToggle: true,
      progressControl: true,
      currentTimeDisplay: true,
      timeDivider: true,
      durationDisplay: true,
      remainingTimeDisplay: true, // 残り時間表示を有効
      volumePanel: true,
      
      // YouTubeライクな機能
      playbackRateMenuButton: true, // 再生速度選択を有効
      pictureInPictureToggle: true, // ピクチャーインピクチャーを有効
      fullscreenToggle: true,
      
      // 不要なコントロールを無効化
      liveDisplay: false,
      seekToLive: false,
      customControlSpacer: false,
      chaptersButton: false,
      descriptionsButton: false,
      subsCapsButton: false,
      audioTrackButton: false
    },
    
    // HTML5設定
    html5: {
      vhs: {
        overrideNative: !deviceInfo.isMobile,
        enableLowInitialPlaylist: true,
        smoothQualityChange: true,
        useBandwidthFromLocalStorage: true,
        allowSeeksWithinUnsafeLiveWindow: true,
        handlePartialData: true,
        segmentTimeout: 30000,
        retryInterval: 2000,
        maxRetries: 5
      }
    },
    
    // エラーハンドリング
    errorDisplay: true,
    
    // テキストトラックを無効化
    textTrackDisplay: false,
    
    // キーボードショートカット（YouTubeライク）
    ...(deviceInfo.isDesktop && {
      plugins: {
        hotkeys: {
          volumeStep: 0.1,
          seekStep: 10,
          enableModifiersForNumbers: false,
          enableVolumeScroll: true, // マウスホイールで音量調整
          enableHoverScroll: true,  // ホバー時のスクロール
          // YouTubeライクなキーバインド
          enableInactiveFocus: false,
          captureDocumentHotkeys: true
        }
      }
    })
  };

  // モバイル専用設定
  if (deviceInfo.isMobile) {
    return {
      ...baseConfig,
      fill: true,
      aspectRatio: 'auto',
      plugins: {
        ...baseConfig.plugins,
        mobileUi: {
          fullscreen: {
            enterOnRotate: false,
            exitOnRotate: false,
            lockOnRotate: false
          },
          touchOverlay: 'auto'
        }
      }
    };
  }

  // デスクトップ専用設定
  if (deviceInfo.isDesktop) {
    return {
      ...baseConfig,
      aspectRatio: '16:9',
      // YouTubeライクな再生速度オプション
      playbackRates: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
    };
  }

  return baseConfig;
};

// 品質レベル設定
export const qualityLevels = {
  '144p': { width: 256, height: 144, bitrate: 200000 },
  '240p': { width: 426, height: 240, bitrate: 400000 },
  '360p': { width: 640, height: 360, bitrate: 800000 },
  '480p': { width: 854, height: 480, bitrate: 1200000 },
  '720p': { width: 1280, height: 720, bitrate: 2500000 },
  '1080p': { width: 1920, height: 1080, bitrate: 5000000 },
  '4K': { width: 3840, height: 2160, bitrate: 15000000 }
};

// ネットワーク状況に応じた推奨品質
export const getRecommendedQuality = (connectionType: string, saveData: boolean = false): string => {
  if (saveData) {
    return '240p';
  }

  const qualityMap = {
    'slow-2g': '144p',
    '2g': '240p',
    '3g': '360p',
    '4g': '720p',
    'wifi': '1080p'
  };

  return qualityMap[connectionType as keyof typeof qualityMap] || '480p';
}; 